import { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Organization } from "types/organization";
import {
  DomainOrganization,
  OrganizationDomainCandidate,
} from "types/organizationDomain";
import { useOrganizationDomains } from "./useOrganizationDomains";

function OrganizationRow({
  organization,
  checked,
  onChange,
}: {
  organization: DomainOrganization;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  const content = (
    <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
      <Avatar src={organization.logo_url || undefined} alt={organization.name}>
        {organization.name?.[0]}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography fontWeight={600}>{organization.name}</Typography>
        {organization.type && (
          <Typography variant="caption" sx={{ textTransform: "capitalize" }}>
            {organization.type}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  return onChange ? (
    <FormControlLabel
      sx={{ display: "flex", m: 0, py: 0.5, width: "100%" }}
      control={
        <Checkbox
          checked={Boolean(checked)}
          onChange={(_, value) => onChange(value)}
        />
      }
      label={content}
    />
  ) : (
    <Box py={0.5}>{content}</Box>
  );
}

function toggleId(ids: string[], id: string, checked: boolean) {
  return checked
    ? Array.from(new Set([...ids, id]))
    : ids.filter((currentId) => currentId !== id);
}

function CandidateCard({
  candidate,
  saving,
  onCreate,
}: {
  candidate: OrganizationDomainCandidate;
  saving: boolean;
  onCreate: (
    name: string,
    orgIds: string[],
    candidatePrefix: string,
  ) => Promise<boolean>;
}) {
  const [name, setName] = useState(
    candidate.suggested_name || candidate.prefix,
  );
  const [selected, setSelected] = useState(
    candidate.organizations.map((organization) => organization.org_id),
  );
  const valid = Boolean(name.trim()) && selected.length >= 2;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6">
              Candidate: {candidate.suggested_name || candidate.prefix}
            </Typography>
            <Typography variant="body2">Prefix: {candidate.prefix}</Typography>
          </Box>
          <TextField
            size="small"
            label="Domain name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {candidate.organizations.map((organization) => (
            <OrganizationRow
              key={organization.org_id}
              organization={organization}
              checked={selected.includes(organization.org_id)}
              onChange={(checked) =>
                setSelected((current) =>
                  toggleId(current, organization.org_id, checked),
                )
              }
            />
          ))}
          {!valid && (
            <Alert severity="info">
              Enter a domain name and select at least two organizations.
            </Alert>
          )}
          <Box>
            <Button
              variant="contained"
              disabled={saving || !valid}
              onClick={() => onCreate(name.trim(), selected, candidate.prefix)}
            >
              Create domain
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DomainForm({
  organizations,
  initialName = "",
  initialMembers = [],
  saving,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  organizations: DomainOrganization[];
  initialName?: string;
  initialMembers?: string[];
  saving: boolean;
  submitLabel: string;
  onSubmit: (name: string, orgIds: string[]) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [selected, setSelected] = useState(initialMembers);
  const valid = Boolean(name.trim()) && selected.length >= 2;

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label="Domain name"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      {organizations.length === 0 ? (
        <Alert severity="info">No organizations are available.</Alert>
      ) : (
        organizations.map((organization) => (
          <OrganizationRow
            key={organization.org_id}
            organization={organization}
            checked={selected.includes(organization.org_id)}
            onChange={(checked) =>
              setSelected((current) =>
                toggleId(current, organization.org_id, checked),
              )
            }
          />
        ))
      )}
      {!valid && (
        <Alert severity="info">
          Enter a domain name and select at least two organizations.
        </Alert>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={saving || !valid}
          onClick={() => onSubmit(name.trim(), selected)}
        >
          {submitLabel}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </Stack>
    </Stack>
  );
}

export function OrganizationDomainsPanel({
  organizations,
}: {
  organizations: Organization[];
}) {
  const {
    domains,
    candidates,
    loadingDomains,
    lookingUp,
    saving,
    error,
    hasLookedUp,
    lookup,
    createDomain,
    updateDomain,
    deleteDomain,
  } = useOrganizationDomains();
  const [lookupName, setLookupName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const allOrganizations = useMemo<DomainOrganization[]>(
    () =>
      organizations
        .filter(
          (organization): organization is Organization & { org_id: string } =>
            Boolean(organization.org_id),
        )
        .map((organization) => ({
          org_id: organization.org_id,
          name: organization.name,
          logo_url: organization.logo_url,
          type: organization.type,
        })),
    [organizations],
  );

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Button
          variant="outlined"
          disabled={lookingUp}
          onClick={() => lookup()}
        >
          Lookup all prefix candidates
        </Button>
        <TextField
          size="small"
          label="Domain name lookup"
          value={lookupName}
          onChange={(event) => setLookupName(event.target.value)}
        />
        <Button
          variant="contained"
          disabled={lookingUp || !lookupName.trim()}
          onClick={() => lookup(lookupName.trim())}
        >
          Lookup name
        </Button>
        {lookingUp && <CircularProgress size={24} aria-label="Looking up" />}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {hasLookedUp && !lookingUp && candidates.length === 0 && (
        <Alert severity="info">No domain candidates found.</Alert>
      )}
      {candidates.map((candidate) => (
        <CandidateCard
          key={candidate.prefix}
          candidate={candidate}
          saving={saving}
          onCreate={createDomain}
        />
      ))}

      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Existing domains</Typography>
        {!creating && (
          <Button variant="contained" onClick={() => setCreating(true)}>
            New domain
          </Button>
        )}
      </Stack>

      {creating && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" mb={1.5}>
              New domain
            </Typography>
            <DomainForm
              organizations={allOrganizations}
              saving={saving}
              submitLabel="Create domain"
              onCancel={() => setCreating(false)}
              onSubmit={async (name, orgIds) => {
                const created = await createDomain(name, orgIds);
                if (created) setCreating(false);
                return created;
              }}
            />
          </CardContent>
        </Card>
      )}

      {loadingDomains ? (
        <CircularProgress aria-label="Loading domains" />
      ) : domains.length === 0 ? (
        <Alert severity="info">No organization domains exist yet.</Alert>
      ) : (
        domains.map((domain) => {
          const editing = editingId === domain.organization_domain_id;
          const selectableOrganizations = Array.from(
            new Map(
              [...allOrganizations, ...domain.organizations].map(
                (organization) => [organization.org_id, organization],
              ),
            ).values(),
          );

          return (
            <Card variant="outlined" key={domain.organization_domain_id}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                  >
                    <Typography variant="h6">
                      {domain.name} ({domain.organizations.length}{" "}
                      organizations)
                    </Typography>
                    {!editing && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          onClick={() =>
                            setEditingId(domain.organization_domain_id)
                          }
                        >
                          Edit domain
                        </Button>
                        <Button
                          color="error"
                          onClick={() =>
                            setDeleteId(domain.organization_domain_id)
                          }
                        >
                          Delete
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  {editing ? (
                    <DomainForm
                      organizations={selectableOrganizations}
                      initialName={domain.name}
                      initialMembers={domain.organizations.map(
                        (organization) => organization.org_id,
                      )}
                      saving={saving}
                      submitLabel="Save domain"
                      onCancel={() => setEditingId(null)}
                      onSubmit={async (name, orgIds) => {
                        const updated = await updateDomain(
                          domain.organization_domain_id,
                          name,
                          orgIds,
                        );
                        if (updated) setEditingId(null);
                        return updated;
                      }}
                    />
                  ) : (
                    domain.organizations.map((organization) => (
                      <OrganizationRow
                        key={organization.org_id}
                        organization={organization}
                      />
                    ))
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })
      )}

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete organization domain?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the domain association. It does not delete any
            organizations.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={async () => {
              if (deleteId && (await deleteDomain(deleteId))) {
                setDeleteId(null);
              }
            }}
          >
            Delete domain
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
